/**
 * Collapsible ingredient groups handler
 *
 * Enables the toggle buttons for ingredient groups and manages
 * expand/collapse state.
 */

/**
 * Update the visual state of a collapsible group
 * @param {HTMLElement} groupElement - The ingredient group container
 * @param {boolean} isCollapsed - Whether the group is collapsed
 */
const updateGroupVisibility = (groupElement, isCollapsed) => {
  const ingredientList = groupElement.querySelector('.mv-create-ingredient-list')
  const toggleButton = groupElement.querySelector('.mv-create-ingredient-group-toggle')

  if (ingredientList) {
    if (isCollapsed) {
      ingredientList.style.display = 'none'
      groupElement.classList.add('mv-checklist-collapsed')
    } else {
      ingredientList.style.display = ''
      groupElement.classList.remove('mv-checklist-collapsed')
    }
  }

  if (toggleButton) {
    toggleButton.setAttribute('aria-expanded', !isCollapsed)
  }
}

/**
 * Initialize collapsible functionality for ingredient groups
 * @param {HTMLElement} cardElement - The Create card container element
 * @param {Function} isGroupCollapsed - Function to check if a group is collapsed
 * @param {Function} toggleGroup - Function to toggle a group's collapsed state
 */
export const initCollapsibleGroups = (
  cardElement,
  isGroupCollapsed,
  toggleGroup
) => {
  // Find all ingredient groups with headers
  const ingredientGroups = cardElement.querySelectorAll(
    '.mv-create-ingredient-group[data-ingredient-group]'
  )

  ingredientGroups.forEach((group) => {
    const groupIndex = group.getAttribute('data-ingredient-group')
    const groupKey = `group_${groupIndex}`
    const header = group.querySelector('.mv-create-ingredient-group-header')
    const toggleButton = group.querySelector('.mv-create-ingredient-group-toggle')

    // Only initialize if there's a header (named groups)
    if (!header || !toggleButton) {
      return
    }

    // Check if already initialized
    if (toggleButton.hasAttribute('data-checklist-initialized')) {
      return
    }

    // Mark as initialized
    toggleButton.setAttribute('data-checklist-initialized', 'true')

    // Show the toggle button (it's hidden by default in the template)
    toggleButton.style.display = ''

    // Get current collapsed state (default: expanded)
    const isCollapsed = isGroupCollapsed(groupKey)

    // Apply initial state
    updateGroupVisibility(group, isCollapsed)

    // Add click handler to the header
    const handleClick = (e) => {
      // Don't trigger if clicking on the toggle button itself (it has its own handler)
      if (e.target === toggleButton) return

      toggleGroup(groupKey)
      const newCollapsedState = !isGroupCollapsed(groupKey)
      updateGroupVisibility(group, newCollapsedState)
    }

    // Add click handler to toggle button
    const handleToggleClick = (e) => {
      e.preventDefault()
      e.stopPropagation()

      toggleGroup(groupKey)
      // Need to re-check state after toggle
      // Since toggleGroup is async-ish through state, we invert current visual state
      const currentlyCollapsed = group.classList.contains('mv-checklist-collapsed')
      updateGroupVisibility(group, !currentlyCollapsed)
    }

    // Make header clickable
    header.style.cursor = 'pointer'
    header.addEventListener('click', handleClick)
    toggleButton.addEventListener('click', handleToggleClick)

    // Store handlers for potential cleanup
    group._checklistHandlers = {
      headerClick: handleClick,
      toggleClick: handleToggleClick,
    }
  })
}

/**
 * Restore collapsible group states from saved state
 * @param {HTMLElement} cardElement - The Create card container element
 * @param {Function} isGroupCollapsed - Function to check if a group is collapsed
 */
export const restoreGroupStates = (cardElement, isGroupCollapsed) => {
  const ingredientGroups = cardElement.querySelectorAll(
    '.mv-create-ingredient-group[data-ingredient-group]'
  )

  ingredientGroups.forEach((group) => {
    const groupIndex = group.getAttribute('data-ingredient-group')
    const groupKey = `group_${groupIndex}`
    const isCollapsed = isGroupCollapsed(groupKey)

    updateGroupVisibility(group, isCollapsed)
  })
}

export default initCollapsibleGroups
